'use client';

import clsx from 'clsx';
import React, { useId } from 'react';
import DSButton from './Button';
import Icon from './Icon';
import { ControlDot, ControlTick, controlBoxClass } from './SelectionControls';

/**
 * DS inputs — measured off node 3161:8399, not taken from the written doc.
 *
 * Verified against Figma rather than §7.2 because Button turned out to have
 * three defects when checked the same way. Two showed up here too: text fields
 * are drawn SQUARE (the doc implies the shared radius), and the Search Input
 * is a PILL with a LEADING icon — not a squared field with a trailing one.
 *
 * Border is the only state affordance, per the system's no-shadow rule:
 *   default  surface-300
 *   hover    surface-400
 *   focus    surface-600
 */

const FIELD_BASE =
    'flex w-full gap-[8px] rounded-none border bg-surface-000 p-[12px] transition-colors focus-within:border-surface-600';
/*
 * @tailwindcss/forms styles bare `input`/`textarea` in the base layer: a
 * grey 1px border, padding, and a blue focus border plus ring. Inside a
 * field container that already draws its own border, that reads as a second
 * border around the control — blue once focused. The container owns the
 * border and the padding, so the control gives up both.
 */
export const fieldControlClass =
    'text-body-400 min-w-0 flex-1 border-0 border-transparent bg-transparent p-0 text-ink-400 outline-none placeholder:text-ink-300 focus:border-0 focus:border-transparent focus:shadow-none focus:ring-0 disabled:cursor-not-allowed disabled:text-surface-400';

export type DSInputProps = {
    /** Right-aligned label rendered INSIDE the field — the signature pattern. */
    label?: string;
    /**
     * Overrides the in-field label's type. The bid input's hint is Body/500 on
     * Text/200 (master 44:103), which is not what the plain field's label is —
     * so it is passed rather than changed for every input.
     */
    labelClassName?: string;
    /** Leading adornment, e.g. the "$" on the bid amount field. */
    prefix?: React.ReactNode;
    /** 20px trailing element — chevron, search glyph. */
    icon?: React.ReactNode;
    multiline?: boolean;
    invalid?: boolean;
    /**
     * Supply the control yourself, keeping the field's chrome. For inputs that
     * have to be a third-party component — the currency-formatted amount on
     * the offer and bid blocks — which would otherwise have to reproduce the
     * border, height and padding by hand and drift from it. Style it with the
     * exported `fieldControlClass`.
     */
    children?: React.ReactNode;
    className?: string;
    containerClassName?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'>;

const DSInput = ({
    label,
    prefix,
    icon,
    multiline = false,
    invalid = false,
    children,
    className,
    containerClassName,
    labelClassName,
    id,
    ...rest
}: DSInputProps) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;

    return (
        <div
            className={clsx(
                FIELD_BASE,
                // Invalid holds its red through focus; the base focus border would otherwise hide it while typing.
                invalid ? 'border-accent-red focus-within:!border-accent-red' : 'border-surface-300 hover:border-surface-400',
                multiline ? 'h-[144px] items-start' : 'h-[48px] items-center',
                containerClassName
            )}
        >
            {/* Master 44:101 — Text/200. The ink ramp has no token for
                #595958; same literal as the suggested-bid label. */}
            {prefix && <span className="text-body-400 shrink-0 self-center text-[#595958]">{prefix}</span>}

            {children || (multiline ? (
                <textarea
                    id={inputId}
                    className={clsx(fieldControlClass, 'size-full resize-none', className)}
                    {...(rest as unknown as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
                />
            ) : (
                <input id={inputId} className={clsx(fieldControlClass, className)} {...rest} />
            ))}

            {label && (
                <label htmlFor={inputId} className={clsx('text-body-300 shrink-0 self-center text-ink-300', labelClassName)}>
                    {label}
                </label>
            )}

            {icon && (
                <span className="flex size-[20px] shrink-0 items-center justify-center self-center text-ink-300">
                    {icon}
                </span>
            )}
        </div>
    );
};

export default DSInput;

/**
 * `Search Input` — a PILL, unlike every other field in the system, with the
 * icon LEADING. Rests on surface-100 at 240 wide and grows to 320 with a
 * surface-200 fill when active. The navbar search is this same treatment.
 *
 * `fluid` drops both widths for a field that lives inside something. The
 * 240→320 growth is a navbar gesture: it assumes open space to the side. In a
 * 292px filter panel there is none, so on focus the field simply grew out
 * through the panel's edges. A className of `w-full` could not hold it —
 * `focus-within:w-[320px]` is a variant, and variants win.
 */
export const SearchInput = ({
    className,
    fluid = false,
    ...rest
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & { fluid?: boolean }) => (
    <div
        className={clsx(
            'flex h-[37px] items-center gap-[8px] rounded-full bg-surface-100 px-[12px]',
            'transition-[width,background-color] focus-within:bg-surface-200',
            fluid ? 'w-full' : 'w-[240px] focus-within:w-[320px]',
            className
        )}
    >
        <Icon name="search" size={18} className="text-ink-300" />
        <input
            placeholder="Search"
            aria-label="Search"
            className={fieldControlClass}
            {...rest}
        />
    </div>
);

/**
 * `Selection Input` — a full-width row with the control on the RIGHT, not a
 * bare checkbox. Fills to surface-200 when hovered or selected.
 */
export const SelectionRow = ({
    label,
    iconUrl,
    selected = false,
    control = 'checkbox',
    disabled = false,
    onChange,
    className
}: {
    label: React.ReactNode;
    iconUrl?: string;
    selected?: boolean;
    control?: 'checkbox' | 'radio';
    disabled?: boolean;
    onChange?: (next: boolean) => void;
    className?: string;
}) => (
    <label
        className={clsx(
            'text-body-400 flex h-[48px] w-full items-center gap-[12px] rounded-ds px-[12px] transition-colors',
            disabled ? 'cursor-not-allowed text-surface-400' : 'cursor-pointer text-ink-400',
            selected && !disabled ? 'bg-surface-200' : !disabled && 'hover:bg-surface-200',
            className
        )}
    >
        {iconUrl && <img src={iconUrl} alt="" aria-hidden className="size-[20px] shrink-0 object-contain" />}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <input
            type={control}
            checked={selected}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.checked)}
            className="peer sr-only"
        />
        {/* The same box the standalone Checkbox and Radio draw — see
            controlBoxClass. This used to be its own copy, which is how it
            ended up without the white ground and the lift. */}
        <span aria-hidden className={controlBoxClass(control)}>
            {control === 'radio' ? <ControlDot /> : <ControlTick />}
        </span>
    </label>
);

/**
 * `Pre-Selection Input` — a segmented option group of 2 or 3. The selected
 * option carries a dark 1px border; the rest stay on surface-300.
 *
 * `compact` draws the options as small filled chips sized to their labels,
 * for when they only feed a field beside them and must not compete with it
 * (the PDP bid block's suggested bids). The selection is a darker fill.
 */
export const PreSelection = <T extends string>({
    options,
    value,
    onChange,
    compact = false,
    className
}: {
    options: { value: T; label: React.ReactNode }[];
    value?: T;
    onChange?: (next: T) => void;
    compact?: boolean;
    className?: string;
}) => (
    <div className={clsx('flex gap-[8px]', compact && 'flex-wrap', className)}>
        {options.map((option) => (
            <button
                key={option.value}
                type="button"
                aria-pressed={value === option.value}
                onClick={() => onChange?.(option.value)}
                className={clsx(
                    'rounded-none transition-colors',
                    compact
                        ? clsx(
                            'text-body-300 h-[32px] px-[12px]',
                            value === option.value
                                ? 'bg-surface-200 text-ink-400'
                                : 'bg-surface-100 text-ink-300 hover:bg-surface-200'
                        )
                        : clsx(
                            'text-body-400 h-[48px] flex-1 border px-[16px] text-ink-400',
                            value === option.value
                                ? 'border-surface-600'
                                : 'border-surface-300 hover:border-surface-400'
                        )
                )}
            >
                {option.label}
            </button>
        ))}
    </div>
);

/**
 * `Bid Input` — 104 tall: a 48px amount field with a leading currency mark and
 * a trailing hint, stacked directly on a full-width Primary CTA with no gap.
 */
export const BidInput = ({
    currency = '$',
    hint,
    ctaLabel,
    onSubmit,
    invalid = false,
    children,
    ctaDisabled = false,
    ctaLoading = false,
    breakdown,
    className,
    ...rest
}: {
    currency?: string;
    hint?: string;
    ctaLabel: string;
    onSubmit?: () => void;
    /** Field border turns accent-red — an amount below the minimum. */
    invalid?: boolean;
    children?: React.ReactNode;
    ctaDisabled?: boolean;
    ctaLoading?: boolean;
    /**
     * Welded between the amount and the CTA, inside the unit's own border.
     *
     * What it costs to bid the entered amount belongs HERE and nowhere else:
     * put it above the field and it is read before there is an amount to
     * price; put it below the button and it is read after the commitment. In
     * this slot it is impossible to reach the CTA without passing it.
     */
    breakdown?: React.ReactNode;
    className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'>) => (
    <div className={clsx('flex w-full flex-col', className)}>
        {/*
          * The field keeps its bottom border unless a breakdown is welded
          * beneath it. The two used to be one continuous box running straight
          * into the CTA, which read as the button overlapping the field — so
          * the button now stands 3px clear, and whatever sits directly above
          * that gap needs an edge of its own to close the box.
          */}
        <DSInput
            prefix={currency}
            label={hint}
            invalid={invalid}
            containerClassName={clsx(!invalid && 'border-surface-200 hover:border-surface-300', breakdown && 'border-b-0')}
            labelClassName="text-body-500 !text-[#595958]"
            {...rest}
        >
            {children}
        </DSInput>
        {breakdown && <div className="border-b border-surface-200">{breakdown}</div>}
        <div className="mt-[3px] flex">
            <DSButton
                variant="primary"
                fullWidth
                disabled={ctaDisabled}
                loading={ctaLoading}
                icon={<Icon name="arrow-right" />}
                onClick={onSubmit}
                className="min-w-0 flex-1"
            >
                {ctaLabel}
            </DSButton>
        </div>
    </div>
);
